import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useAuthStore from "../../store/useAuthStore";
import { delayedToast, isButtonDisabled } from "../../lib/customToast.js";
import GenericModal from "../common/GenericModal";
import { User } from "lucide-react";

/**
 * RealEstateNavbar - A navbar specifically for the Real Estate / Housing Plots section.
 * It mimics the Homepage navbar behavior: transparent at top, fixed white on scroll.
 */
const RealEstateNavbar = ({ forceWhite = false }) => {
    const [showFixedNav, setShowFixedNav] = useState(forceWhite);
    const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { token, userType, user } = useAuthStore();

    useEffect(() => {
        if (forceWhite) {
            setShowFixedNav(true);
            return;
        }
        const handleScroll = () => {
            // Show fixed nav after scrolling 100px
            setShowFixedNav(window.scrollY > 100);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, [forceWhite]);

    const handleLoginRedirect = (selectedUserType) => {
        if (token && userType) {
            if (userType !== selectedUserType) {
                delayedToast.error(`You are already logged in as a ${userType}.`, {}, "navbar-login-redirect-btn");
                return;
            }
            if (userType === "agent") navigate("/agent/profile");
            else if (userType === "builder") navigate("/builder/dashboard");
            else navigate("/customer/projectinvite");
        } else {
            navigate(`/agent/login`); // Default to agent login for this navbar
        }
    };

    const handleSignupCardClick = (signupUserType) => {
        setIsSignupModalOpen(false);
        if (signupUserType === "agent") {
            navigate("/agent/signup");
        } else {
            navigate(signupUserType === "builder" ? "/builder/signup" : "/customer/signup");
        }
    };

    const navLinks = [
        { name: "HOME", path: "/" },
        { name: "SERVICES", path: "/#services" },
        { name: "SUPPORT", path: "/support" },
    ];

    const NavItem = ({ item, isTransparent }) => {
        const textColor = isTransparent ? "text-white hover:text-gray-300" : "text-black hover:text-gray-500 hover:bg-gray-100";

        const handleClick = (e) => {
            if (item.path.startsWith("/#")) {
                e.preventDefault();
                if (location.pathname !== "/") {
                    navigate("/", { state: { scrollToServices: true } });
                } else {
                    document.querySelector(item.path.substring(1))?.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                    });
                }
            } else {
                navigate(item.path);
            }
        };

        return (
            <button
                onClick={handleClick}
                className={`relative px-4 py-2 text-[14px] font-bold font-poppins transition-all hover:rounded-full duration-300 ${textColor}`}
            >
                {item.name}
            </button>
        );
    };

    const AuthButton = ({ isTransparent }) => {
        const isAgent = token && userType === "agent";

        if (isAgent && user) {
            return (
                <button
                    onClick={() => navigate("/agent/profile")}
                    className="flex items-center gap-2 bg-blue-600 text-white font-bold font-poppins px-4 py-2 rounded-full hover:bg-blue-700 transition-colors"
                >
                    <User className="w-4 h-4" />
                    Hello, {user.name?.split(" ")[0] || "Agent"}
                </button>
            );
        }

        return (
            <button
                onClick={() => navigate("/agent/login")}
                className="bg-blue-600 text-white font-bold font-poppins px-6 py-2 rounded-full hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                disabled={isButtonDisabled("navbar-login-redirect-btn")}
            >
                Agent Login
            </button>
        );
    };

    return (
        <>
            {/* Transparent Navbar (Top) */}
            <div className={`absolute top-0 left-0 right-0 z-50 transition-opacity duration-300 ${showFixedNav ? "opacity-0 pointer-events-none" : "opacity-100"}`}>
                <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-20">
                        <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate("/")}>
                            <img src="/assets/Transparent background.png" alt="Ducktail" className="h-16 lg:h-20" />
                        </div>
                        <div className="hidden lg:flex space-x-3 items-center">
                            {navLinks.map((link) => <NavItem key={link.name} item={link} isTransparent={true} />)}
                            <button
                                onClick={() => setIsSignupModalOpen(true)}
                                className="relative px-4 py-2 text-[14px] font-bold font-poppins transition-all hover:rounded-full duration-300 text-white hover:text-gray-300"
                            >
                                JOIN US
                            </button>
                            <div className="ml-10">
                                <AuthButton isTransparent={true} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fixed White Navbar (Scrolled) */}
            <div className={`fixed top-0 left-0 right-0 z-[100] bg-white shadow-md transition-all duration-300 transform ${showFixedNav ? "translate-y-0" : "-translate-y-full"}`}>
                <div className="max-w-[90rem] mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate("/")}>
                            <img src="/assets/black logo.png" alt="Ducktail" className="h-12" />
                        </div>
                        <div className="hidden lg:flex space-x-3 items-center">
                            {navLinks.map((link) => <NavItem key={link.name} item={link} isTransparent={false} />)}
                            <button
                                onClick={() => setIsSignupModalOpen(true)}
                                className="relative px-4 py-2 text-[14px] font-bold font-poppins transition-all hover:rounded-full duration-300 text-black hover:text-gray-500 hover:bg-gray-100"
                            >
                                JOIN US
                            </button>
                            <div className="ml-10">
                                <AuthButton isTransparent={false} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Join Us Modal */}
            <GenericModal
                isOpen={isSignupModalOpen}
                onClose={() => setIsSignupModalOpen(false)}
                title="Join Ducktail As"
                size="max-w-xl"
            >
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 p-4">
                    <div
                        onClick={() => handleSignupCardClick("agent")}
                        className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
                    >
                        <img src="/assets/agent-icon.png" alt="Agent" className="w-16 h-16 object-contain mb-3" onError={(e) => e.target.src = "/assets/builder.png"} />
                        <h3 className="text-md font-semibold text-gray-700">Agent</h3>
                        <p className="text-[10px] text-gray-500 text-center mt-1">List properties and find buyers.</p>
                    </div>
                    <div
                        onClick={() => handleSignupCardClick("builder")}
                        className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
                    >
                        <img src="/assets/builder.png" alt="Builder" className="w-16 h-16 object-contain mb-3" />
                        <h3 className="text-md font-semibold text-gray-700">Builder</h3>
                        <p className="text-[10px] text-gray-500 text-center mt-1">Showcase projects and connect with clients.</p>
                    </div>
                    <div
                        onClick={() => handleSignupCardClick("customer")}
                        className="flex flex-col items-center p-4 border border-gray-300 rounded-lg hover:shadow-lg hover:border-blue-500 transition-all duration-200 cursor-pointer bg-gray-50"
                    >
                        <img src="/assets/young-man.png" alt="Customer" className="w-16 h-16 object-contain mb-3" />
                        <h3 className="text-md font-semibold text-gray-700">Customer</h3>
                        <p className="text-[10px] text-gray-500 text-center mt-1">Find verified builders for your project.</p>
                    </div>
                </div>
            </GenericModal>
        </>
    );
};

export default RealEstateNavbar;
